import {
  useComments,
  useAddComment,
  useEditComment,
  useDeleteComment,
  mapCommentDtoToEnhancedComment,
  CommentsSortOrder,
  CommentsSortBy,
  CommentsCategory
} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {headlineSx} from '@hconnect/common/styles'
import {getSystemTimezone} from '@hconnect/common/utils'
import {useUserContext} from '@hconnect/login'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {CommentProps, ControlledCommentBox} from '@hm/fundamental'
import {Box, Divider} from '@mui/material'
import {useCallback} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  commentCategoryInstanceId: string
}

export const ProcessWorkOrderCommentsQueryKeyPrefix = 'process-work-order-comments'

export const ProcessWorkOrderCommentsContainer = ({plantId, commentCategoryInstanceId}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const timezone = getSystemTimezone()
  const {loggedUser} = useUserContext()
  const currentUserName = loggedUser?.name
  const commentsCategory = CommentsCategory.ProcessWorkOrders
  const commentsSortOrder = CommentsSortOrder.Descending
  const commentsSortBy = CommentsSortBy.CreatedOn

  const {control, reset} = useForm<{
    comment: string
  }>({defaultValues: {comment: ''}})

  const {
    data: comments,
    isInitialLoading,
    isError,
    isRefetching,
    refetch
  } = useComments(
    {
      plantId,
      commentCategoryInstanceIds: [commentCategoryInstanceId],
      commentsCategory,
      commentsSortOrder,
      commentsSortBy
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )
  const {mutateAsync: editComment} = useEditComment(
    {
      plantId,
      commentsCategory
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId,
      commentsCategory
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )
  const {mutateAsync: deleteComment} = useDeleteComment(
    {
      plantId,
      commentsCategory
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )

  const handleDeleteComment = useCallback(
    async (id: string) => {
      await deleteComment({id, commentCategoryInstanceId})
    },
    [deleteComment, commentCategoryInstanceId]
  )

  const handleEditComment = useCallback(
    async ({id}: CommentProps<string>, value: string) => {
      await editComment({value, id, commentCategoryInstanceId})
    },
    [editComment, commentCategoryInstanceId]
  )

  const handleAddComment = useCallback(
    async (commentInputValue: string) => {
      const trimmed = commentInputValue?.trim() ?? ''
      if (!trimmed) return
      await addComment({value: trimmed, commentCategoryInstanceId})
      reset()
    },
    [addComment, commentCategoryInstanceId, reset]
  )

  const enhancedComments =
    comments?.map((comment) =>
      mapCommentDtoToEnhancedComment(comment, timezone, language, currentUserName)
    ) ?? []

  return (
    <DataContentWrapper
      showLoader={isInitialLoading}
      isError={isError}
      isNoData={false} // we never show 'No data' cause we need to show the input to add first comment
      isRefetching={isRefetching}
      retryFunction={refetch}
      progressSx={{color: 'primary.main'}}
    >
      <Box>
        <CardSectionTitle sx={headlineSx()}>
          {/* TODO: in future make this common event/Task translations */}
          {t('shiftEvent.label.comments')}
        </CardSectionTitle>
        <Divider sx={{borderColor: 'divider', mb: 1}} />
        <div className="relative">
          <Controller
            name="comment"
            control={control}
            render={({field}) => (
              <ControlledCommentBox
                comments={enhancedComments}
                commentInputValue={field.value}
                onCommentInputChange={field.onChange}
                onDelete={handleDeleteComment}
                onEdit={handleEditComment}
                onSubmit={handleAddComment}
              />
            )}
          />
        </div>
      </Box>
    </DataContentWrapper>
  )
}
